import React, { useEffect, useMemo, useState } from "react";
import { apiUrl } from "../lib/apiBase";
import { PLATFORM_EMOTES } from "../lib/platformEmotes";

function getToken() {
  try {
    return (
      localStorage.getItem("ga_token") ||
      localStorage.getItem("token") ||
      localStorage.getItem("authToken") ||
      localStorage.getItem("jwt") ||
      ""
    );
  } catch {
    return "";
  }
}

function requestKey(prefix) {
  try {
    return prefix + ":" + crypto.randomUUID();
  } catch {
    return (
      prefix +
      ":" +
      Date.now().toString(36) +
      ":" +
      Math.random().toString(36).slice(2)
    );
  }
}

export default function GasPointsStoreModal({
  open,
  onClose,
  channelKey,
  account,
  onAccountChange,
}) {
  const [message, setMessage] = useState("");
  const [selectedEmote, setSelectedEmote] = useState(
    PLATFORM_EMOTES[0]?.code || ""
  );
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const config = account?.config || {};
  const balance = Math.max(0, Number(account?.balance || 0));
  const messageCost = Math.max(
    0,
    Number(config.streamMessageOverlayCost || 750)
  );
  const emoteCost = Math.max(
    0,
    Number(config.streamEmoteOverlayCost || 350)
  );

  useEffect(() => {
    if (!open) return;
    setError("");
    setNotice("");
  }, [open]);

  const selected = useMemo(
    () =>
      PLATFORM_EMOTES.find((emote) => emote.code === selectedEmote) ||
      PLATFORM_EMOTES[0],
    [selectedEmote]
  );

  if (!open) return null;

  async function purchase(path, body, kind) {
    if (busy) return;
    setBusy(kind);
    setError("");
    setNotice("");
    try {
      const response = await fetch(apiUrl(path), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + getToken(),
        },
        body: JSON.stringify(body),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data?.ok) {
        throw new Error(
          data?.message || "That GAS Points purchase could not be completed."
        );
      }
      if (data.account) onAccountChange?.(data.account);
      setNotice(
        kind === "message"
          ? "Your personal message was sent to the live stream."
          : "Your GA emote was sent to the live stream."
      );
      if (kind === "message") setMessage("");
    } catch (purchaseError) {
      setError(
        purchaseError?.message ||
          "That GAS Points purchase could not be completed."
      );
    } finally {
      setBusy("");
    }
  }

  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        zIndex: 82,
        borderRadius: 14,
        background: "rgba(0,0,0,0.72)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 10,
      }}
      onClick={() => {
        if (!busy) onClose?.();
      }}
    >
      <div
        onClick={(event) => event.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: 430,
          maxHeight: "92%",
          overflowY: "auto",
          borderRadius: 18,
          border: "1px solid rgba(255,205,70,0.24)",
          background:
            "radial-gradient(500px 280px at 20% 0%, rgba(255,184,0,0.17), transparent 65%), linear-gradient(180deg, rgba(18,18,20,0.99), rgba(8,8,10,0.98))",
          boxShadow: "0 22px 60px rgba(0,0,0,0.62)",
          padding: 15,
          color: "white",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 12,
          }}
        >
          <div>
            <div style={{ fontWeight: 1000, fontSize: 18 }}>
              ⛽ GAS Points
            </div>
            <div
              style={{
                marginTop: 5,
                color: "#ffd65c",
                fontSize: 22,
                fontWeight: 1000,
              }}
            >
              {balance.toLocaleString()}
            </div>
            <div style={{ opacity: 0.7, fontSize: 11, marginTop: 2 }}>
              Get Active Streaming Points
            </div>
          </div>
          <button
            type="button"
            onClick={() => {
              if (!busy) onClose?.();
            }}
            aria-label="Close GAS Points"
            style={{
              border: "none",
              background: "transparent",
              color: "white",
              fontSize: 20,
              fontWeight: 900,
              cursor: busy ? "wait" : "pointer",
            }}
          >
            ✕
          </button>
        </div>

        <div
          style={{
            marginTop: 12,
            padding: "9px 10px",
            borderRadius: 12,
            background: "rgba(255,255,255,0.055)",
            fontSize: 11,
            lineHeight: 1.45,
            opacity: 0.84,
          }}
        >
          Earn {Number(config.watchReward || 10)} points every{" "}
          {Math.round(Number(config.watchIntervalMs || 300000) / 60000)} active
          watch minutes and {Number(config.rpsWinReward || 100)} points for an
          RPS win. Future paid-entry tournaments can also accept GAS Points;
          the current free tournament stays free.
        </div>

        <div
          style={{
            marginTop: 14,
            paddingTop: 13,
            borderTop: "1px solid rgba(255,255,255,0.09)",
          }}
        >
          <div style={{ fontWeight: 950 }}>Personal message overlay</div>
          <div style={{ marginTop: 3, opacity: 0.72, fontSize: 11 }}>
            One use on this live stream • {messageCost.toLocaleString()} points
          </div>
          <textarea
            value={message}
            onChange={(event) => setMessage(event.target.value.slice(0, 90))}
            placeholder="Type the message viewers will see…"
            maxLength={90}
            disabled={Boolean(busy)}
            style={{
              width: "100%",
              minHeight: 72,
              resize: "none",
              marginTop: 9,
              borderRadius: 12,
              border: "1px solid rgba(255,255,255,0.12)",
              background: "rgba(255,255,255,0.06)",
              color: "white",
              padding: 10,
              outline: "none",
              font: "inherit",
              boxSizing: "border-box",
            }}
          />
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: 8,
              alignItems: "center",
              marginTop: 7,
            }}
          >
            <span style={{ opacity: 0.58, fontSize: 10 }}>
              {message.length}/90
            </span>
            <button
              type="button"
              disabled={
                Boolean(busy) ||
                message.trim().length < 2 ||
                balance < messageCost
              }
              onClick={() =>
                purchase(
                  "/gas-points/overlay/message",
                  {
                    channelSlug: channelKey,
                    message: message.trim(),
                    idempotencyKey: requestKey("stream-message"),
                  },
                  "message"
                )
              }
              style={{
                minHeight: 40,
                borderRadius: 12,
                border: "1px solid rgba(255,214,92,0.25)",
                background:
                  balance >= messageCost && message.trim().length >= 2
                    ? "linear-gradient(135deg, #b87400, #d63131)"
                    : "rgba(255,255,255,0.06)",
                color: "white",
                padding: "0 13px",
                fontWeight: 950,
                cursor:
                  balance >= messageCost && message.trim().length >= 2 && !busy
                    ? "pointer"
                    : "not-allowed",
              }}
            >
              {busy === "message"
                ? "Sending…"
                : "Spend " + messageCost.toLocaleString() + " Points"}
            </button>
          </div>
        </div>

        <div
          style={{
            marginTop: 14,
            paddingTop: 13,
            borderTop: "1px solid rgba(255,255,255,0.09)",
          }}
        >
          <div style={{ fontWeight: 950 }}>GA emote overlay</div>
          <div style={{ marginTop: 3, opacity: 0.72, fontSize: 11 }}>
            One animated appearance • {emoteCost.toLocaleString()} points
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(6, minmax(0, 1fr))",
              gap: 5,
              marginTop: 9,
            }}
          >
            {PLATFORM_EMOTES.map((emote) => {
              const active = selected?.code === emote.code;
              return (
                <button
                  type="button"
                  key={emote.key}
                  onClick={() => setSelectedEmote(emote.code)}
                  title={emote.label}
                  aria-label={"Select " + emote.label}
                  style={{
                    minWidth: 0,
                    height: 45,
                    borderRadius: 9,
                    border: active
                      ? "1px solid rgba(255,214,92,0.72)"
                      : "1px solid rgba(255,255,255,0.08)",
                    background: active
                      ? "rgba(255,184,0,0.13)"
                      : "rgba(255,255,255,0.04)",
                    padding: 3,
                    cursor: "pointer",
                  }}
                >
                  <img
                    src={emote.src}
                    alt=""
                    style={{
                      display: "block",
                      width: "100%",
                      height: "100%",
                      objectFit: "contain",
                    }}
                  />
                </button>
              );
            })}
          </div>
          <button
            type="button"
            disabled={Boolean(busy) || !selected || balance < emoteCost}
            onClick={() =>
              purchase(
                "/gas-points/overlay/emote",
                {
                  channelSlug: channelKey,
                  emoteCode: selected?.code,
                  idempotencyKey: requestKey("stream-emote"),
                },
                "emote"
              )
            }
            style={{
              width: "100%",
              minHeight: 42,
              marginTop: 9,
              borderRadius: 12,
              border: "1px solid rgba(255,214,92,0.25)",
              background:
                selected && balance >= emoteCost
                  ? "linear-gradient(135deg, #8d4d00, #bc1f55)"
                  : "rgba(255,255,255,0.06)",
              color: "white",
              padding: "0 12px",
              fontWeight: 950,
              cursor:
                selected && balance >= emoteCost && !busy
                  ? "pointer"
                  : "not-allowed",
            }}
          >
            {busy === "emote"
              ? "Sending…"
              : "Put " +
                (selected?.label || "emote") +
                " on stream • " +
                emoteCost.toLocaleString()}
          </button>
        </div>

        {error ? (
          <div
            style={{
              marginTop: 11,
              color: "#ffb1b1",
              fontSize: 12,
              fontWeight: 850,
            }}
          >
            {error}
          </div>
        ) : null}
        {notice ? (
          <div
            style={{
              marginTop: 11,
              color: "#8cffb2",
              fontSize: 12,
              fontWeight: 850,
            }}
          >
            {notice}
          </div>
        ) : null}
      </div>
    </div>
  );
}

